import { Button } from "@components/ui/Button"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { SquadupOptions } from "@features/ticketing/squadup/squadup-options"
import { SquadupEmbed } from "@features/ticketing/SquadUpEmbed"
import { useSquadup } from "@features/ticketing/useSquadUp"
import { tv } from "@utils/tw"
import { motion } from "motion/react"
import { ReactNode, useEffect, useRef, useState } from "react"

export type Props = {
  squadupEventId: number
  enabled?: boolean
  bookLabel?: ReactNode
  heading?: ReactNode
  description?: ReactNode
  squadupOptions?: Partial<SquadupOptions>
}

export function GenericSquadupTicketWidget(props: Props) {
  const flyingCarpet = useFlyingCarpet()

  const squadupEventId = props.squadupEventId
  const squadup = useSquadup()

  const expander = useRef<HTMLDivElement>(null!)
  const [state, setState] = useState<"idle" | "loading" | "expanded">("idle")

  useEffect(() => {
    if (state === "loading") {
      const timeout = setTimeout(() => {
        const uri = new URL(window.location.href)
        uri.searchParams.set("opentickets", "1")
        document.location.href = uri.toString()
      }, 6000)
      return () => clearTimeout(timeout)
    }
  }, [state])

  const styles = createStyles()

  return (
    <div className={styles.base()}>
      <div className={styles.card()}>
        <div className={styles.info()}>
          <p className="flex gap-2 type-body-m-bold">{props.heading}</p>
          <p className="type-body-s flex items-center gap-2">{props.description}</p>
        </div>
        <div className={styles.buttons()}>
          <Button
            className="flex-1 md:justify-start"
            disabled={!squadupEventId || !props.enabled}
            leftIcon="ticket"
            onClick={() => {
              if (!squadupEventId || !props.enabled) return
              if (state !== "expanded") {
                squadup.open({
                  eventId: squadupEventId,
                  showCheckout: false,
                  ...props.squadupOptions,
                })
              } else {
                setState("idle")
                squadup.close()
              }
            }}
            loading={state === "loading"}
          >
            {state === "expanded" ? "Close" : (props.bookLabel ?? "Book Now")}
          </Button>
        </div>
      </div>
      <motion.div
        className={styles.expander()}
        variants={{
          expanded: { height: "auto" },
          idle: { height: 0 },
          loading: { height: 0 },
        }}
        transition={{ ease: "easeOut" }}
        animate={state}
        ref={expander}
        onAnimationStart={(def) => {
          expander.current.style.overflow = "hidden"
          expander.current.style.display = "block"
        }}
        onAnimationComplete={(def) => {
          if (state === "expanded") {
            expander.current.style.overflow = "visible"
          } else {
            expander.current.style.display = "none"
          }
        }}
      >
        <SquadupEmbed
          className="embedded-event-tickets"
          enabled
          name="event"
          getPriority={(state) => {
            if (flyingCarpet.isOpen) return -1
            let priority = 0
            if (state.currentView === "eventDetail" || state.currentView === "seatingChart") {
              priority += 10
              if (state.viewingEvent?.id === squadupEventId) {
                priority += 5
              }
            }
            return priority
          }}
          onVisibleChange={(visible, loading) => setState(loading ? "loading" : visible ? "expanded" : "idle")}
        />
      </motion.div>
    </div>
  )
}

const createStyles = tv({
  slots: {
    base: ["flex flex-col"],
    card: ["flex flex-col bg-yellow gap-3 py-3 px-3", "md:flex-row md:items-center md:justify-between"],
    info: ["flex flex-col gap-0 pl-1"],
    buttons: "flex gap-3 flex-none",
    expander: ["border-2 border-yellow border-t-0 will-change-transform"],
  },
})
